import { useContext, useMemo, type FC } from 'react';
import InlineOptions from '../../inline-options';
import Chart from '../../chart';
import {
  ImpliedRealizedVolatilityContext,
  METRIC_OPTIONS,
  TENOR_OPTIONS,
} from '../../../../providers/widgets';
import { MetricEnum, WindowEnum } from '../../../../providers/volatility-cones-provider';
import Divider from '../../divider';
import './styles.scss';
import TimestampInfo from '../../timestamp-info';

const ImpliedRealizedVolatilityContent: FC = () => {
  const {
    data: responseData,
    error,
    isLoading,
    tenor,
    setTenor,
    metric,
    setMetric,
  } = useContext(ImpliedRealizedVolatilityContext);

  const data = responseData?.data || [];

  const renderInlineOptions = () => (
    <>
      <div className="implied-realized-volatility-header">
        <InlineOptions
          options={TENOR_OPTIONS}
          selectedOption={tenor}
          onOptionChange={(val) => setTenor(val as WindowEnum)}
          maxVisibleOptions={5}
        />
        <Divider />
        <InlineOptions
          options={METRIC_OPTIONS}
          selectedOption={metric}
          onOptionChange={(val) => setMetric(val as MetricEnum)}
          maxVisibleOptions={5}
        />
      </div>
      {!error && responseData?.asAtTime && (
        <div className="implied-realized-volatility-timestamp">
          <TimestampInfo timestamp={new Date(responseData?.asAtTime || '').getTime()} />
        </div>
      )}
    </>
  );

  const colorBySymbol: Record<string, string> = {
    BTC: '#F97316',
    ETH: '#A78BFA',
    SOL: '#22D3EE',
    XRP: '#FB7185',
  };

  const points = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .filter(
          (d: any) => d && typeof d.ivol === 'number' && typeof d.rvol === 'number' && d.symbol,
        )
        .map((d: any) => {
          const symbol = String(d.symbol).toUpperCase();
          return {
            label: symbol,
            x: d.rvol,
            y: d.ivol,
            color: colorBySymbol[symbol] ?? '#999999',
          };
        }),
    [data],
  );

  const xAxis = useMemo(
    () => ({
      type: 'value' as const,
      min: 0,
      max: 1,
      axisLabel: {
        formatter: (v: number) => `${Math.round(v * 100)}`,
      },
      formatter: (v: number) => `${Math.round(v * 100)}`,
    }),
    [],
  );

  const yAxis = useMemo(
    () => ({
      type: 'value' as const,
      min: 0,
      max: 1,
      axisLabel: {
        formatter: (v: number) => `${Math.round(v * 100)}`,
      },
    }),
    [],
  );

  const series = useMemo(
    () => [
      {
        type: 'line' as const,
        data: [
          [0, 0],
          [1, 1],
        ],
        lineStyle: {
          color: '#002649',
          width: 2,
        },
        symbol: 'none',
        silent: true,
        z: 0,
      },
      ...points.map((p) => ({
        type: 'scatter' as const,
        data: [[p.x, p.y]],
        color: p.color,
        name: p.label,
        key: p.label,
      })),
    ],
    [points],
  );

  return (
    <div className="implied-realized-volatility-container">
      {renderInlineOptions()}
      {error && (
        <div className="implied-realized-volatility-no-data">
          <span>No Data Found.</span>
        </div>
      )}
      {!error && (
        <Chart
          series={series}
          xAxis={xAxis}
          yAxis={yAxis}
          tooltip={{
            valueFormatter: (value: any) => `${(value * 100).toFixed(2)}%`,
          }}
          className="implied-realized-volatility-chart"
          isLoading={isLoading}
          merge={false}
        />
      )}
    </div>
  );
};
export default ImpliedRealizedVolatilityContent;
